import { Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { ThrottleService } from '@services/utilities/1.0/throttle.service';

const INACTIVITY_TIMEOUT_LENGTH = 1000 * 60 * 60; // 1 hour (ORIGINAL - revert after testing)
const INACTIVITY_KEY = 'inactiveTime';

@Injectable({
  providedIn: 'root',
})
class InactivityService {
  checkInterval: ReturnType<typeof setInterval>;
  updateupdateInactivityTimeout_throttled = ThrottleService.throttle(
    this.updateInactivityTimeout,
    60000,
  );
  private boundVisibilityHandler: () => void;

  constructor(private authService: AuthService) {}

  init() {
    this.updateupdateInactivityTimeout_throttled();

    // https://developer.mozilla.org/en-US/docs/Web/API/setInterval#the_this_problem
    const self = this;
    this.checkInterval = setInterval(() => {
      this.checkIfInactive(self);
    }, 1000 * 60); // 1 minute

    window.addEventListener('keydown', () => {
      this.updateupdateInactivityTimeout_throttled();
    });
    window.addEventListener('mousemove', () => {
      this.updateupdateInactivityTimeout_throttled();
    });

    // TCE-21020: Check timeout when page becomes visible (handles laptop sleep/wake)
    // setInterval is throttled when tab is inactive, so we check immediately on visibility change
    this.boundVisibilityHandler = () => {
      if (!document.hidden) {
        // Page became visible - check if timeout expired while sleeping
        this.checkIfInactive(self);
      }
    };
    document.addEventListener('visibilitychange', this.boundVisibilityHandler);
  }

  checkIfInactive(self: InactivityService) {
    const currentTime = new Date().getTime();
    const inactivityTimeout = window.sessionStorage.getItem(INACTIVITY_KEY);

    if (!inactivityTimeout) {
      return;
    }

    const remainingMs = parseInt(inactivityTimeout, 10) - currentTime;

    if (currentTime < parseInt(inactivityTimeout, 10)) return;
    // @ts-ignore: see ComponentCanDeactivate directive
    window.__user_is_inactive__ = true;
    window.onbeforeunload = null;
    self.teardown();

    // TCE-21020: Clear session and redirect to home instead of federated logout
    // This avoids getting stuck on Microsoft OAuth screen and shows TCE login directly
    window.sessionStorage.clear();
    window.localStorage.clear();
    window.location.href = window.location.origin + '?timeout=true';
  }

  teardown() {
    clearInterval(this.checkInterval);
    window.removeEventListener('keydown', this.updateupdateInactivityTimeout_throttled);
    window.removeEventListener('mousemove', this.updateupdateInactivityTimeout_throttled);
    if (this.boundVisibilityHandler) {
      document.removeEventListener('visibilitychange', this.boundVisibilityHandler);
    }
  }

  updateInactivityTimeout() {
    const currentTime = new Date().getTime();
    const existingTimeout = window.sessionStorage.getItem(INACTIVITY_KEY);

    if (existingTimeout && currentTime >= parseInt(existingTimeout, 10)) {
      return;
    }

    const inactivityTimeout = currentTime + INACTIVITY_TIMEOUT_LENGTH;
    window.sessionStorage.setItem(INACTIVITY_KEY, inactivityTimeout.toString());
  }

  /**
   * Public method to manually update inactivity timeout.
   * Used by components with iframes where window events don't propagate (e.g., Reports module).
   * TCE-21020: Allow iframe-based components to extend the timeout manually.
   */
  refreshActivity(source?: string) {
    this.updateInactivityTimeout();
  }
}
export { InactivityService };
